import { Category } from './index';

/**
 * WhatsApp Business API Pricing per message (in INR)
 * Based on Meta's pricing structure effective January 1, 2026
 *
 * Regions:
 * - Explicitly priced: AR, BR, CL, CO, EG, FR, DE, IN, ID, IL, IT, MY, MX, NL, NG, PK, PE, RU, SA, ZA, ES, TR, AE, GB
 * - North America (US, CA)
 * - Rest of Africa
 * - Rest of Asia Pacific
 * - Rest of Central & Eastern Europe
 * - Rest of Latin America
 * - Rest of Middle East
 * - Rest of Western Europe
 * - Other (Rest of the World)
 */

export type CategoryPricing = {
  [Category.MARKETING]: number;
  [Category.UTILITY]: number;
  [Category.AUTHENTICATION]: number;
};

// Partial pricing for custom overrides (not all categories required)
export type PartialCategoryPricing = Partial<CategoryPricing>;

// Custom pricing overrides by country ISO code
// Example: { "IN": { "MARKETING": 0.75 }, "US": { "MARKETING": 1.00, "UTILITY": 0.50 } }
export type CustomPricing = Record<string, PartialCategoryPricing>;

export type WhatsAppPricing = Record<string, CategoryPricing>;

/**
 * Complete WhatsApp Business API pricing by country ISO code (uppercase)
 * All prices are in INR per message
 * Effective January 1, 2026
 */
export const WHATSAPP_PRICING: WhatsAppPricing = {
  // ============================================
  // EXPLICITLY PRICED COUNTRIES (from Meta)
  // ============================================

  // Argentina
  AR: { MARKETING: 4.5293, UTILITY: 1.9062, AUTHENTICATION: 1.9062 },
  // Brazil
  BR: { MARKETING: 4.5808, UTILITY: 0.4984, AUTHENTICATION: 0.4984 },
  // Chile
  CL: { MARKETING: 6.5135, UTILITY: 1.4654, AUTHENTICATION: 1.4654 },
  // Colombia
  CO: { MARKETING: 0.9161, UTILITY: 0.0588, AUTHENTICATION: 0.0588 },
  // Egypt
  EG: { MARKETING: 4.7191, UTILITY: 0.2639, AUTHENTICATION: 0.2639 },
  // France
  FR: { MARKETING: 6.299, UTILITY: 2.1994, AUTHENTICATION: 2.1994 },
  // Germany
  DE: { MARKETING: 10.0073, UTILITY: 4.0322, AUTHENTICATION: 4.0322 },
  // India
  IN: { MARKETING: 0.8631, UTILITY: 0.115, AUTHENTICATION: 0.115 },
  // Indonesia
  ID: { MARKETING: 3.0111, UTILITY: 1.8316, AUTHENTICATION: 1.8316 },
  // Israel
  IL: { MARKETING: 2.5871, UTILITY: 0.3885, AUTHENTICATION: 0.3885 },
  // Italy
  IT: { MARKETING: 5.0614, UTILITY: 2.1974, AUTHENTICATION: 2.1974 },
  // Malaysia
  MY: { MARKETING: 6.3037, UTILITY: 1.0262, AUTHENTICATION: 1.0262 },
  // Mexico
  MX: { MARKETING: 2.2357, UTILITY: 0.6226, AUTHENTICATION: 0.6226 },
  // Netherlands
  NL: { MARKETING: 11.7078, UTILITY: 3.6656, AUTHENTICATION: 3.6656 },
  // Nigeria
  NG: { MARKETING: 3.7851, UTILITY: 0.4915, AUTHENTICATION: 0.4915 },
  // Pakistan
  PK: { MARKETING: 3.4683, UTILITY: 0.396, AUTHENTICATION: 0.396 },
  // Peru
  PE: { MARKETING: 5.1536, UTILITY: 1.4662, AUTHENTICATION: 1.4662 },
  // Russia
  RU: { MARKETING: 5.8767, UTILITY: 2.9311, AUTHENTICATION: 2.9311 },
  // Saudi Arabia
  SA: { MARKETING: 3.3292, UTILITY: 0.7834, AUTHENTICATION: 0.7834 },
  // South Africa
  ZA: { MARKETING: 2.7821, UTILITY: 0.5579, AUTHENTICATION: 0.5579 },
  // Spain
  ES: { MARKETING: 4.5044, UTILITY: 1.4648, AUTHENTICATION: 1.4648 },
  // Turkey
  TR: { MARKETING: 0.7989, UTILITY: 0.3884, AUTHENTICATION: 0.3884 },
  // United Arab Emirates
  AE: { MARKETING: 3.6613, UTILITY: 1.1509, AUTHENTICATION: 1.1509 },
  // United Kingdom
  GB: { MARKETING: 3.8747, UTILITY: 1.6122, AUTHENTICATION: 1.6122 },

  // ============================================
  // NORTH AMERICA
  // Pricing: MARKETING: 1.8313, UTILITY: 0.2491, AUTHENTICATION: 0.2491
  // ============================================

  // Canada
  CA: { MARKETING: 1.8313, UTILITY: 0.2491, AUTHENTICATION: 0.2491 },
  // United States
  US: { MARKETING: 1.8313, UTILITY: 0.2491, AUTHENTICATION: 0.2491 },

  // ============================================
  // REST OF AFRICA
  // Pricing: MARKETING: 1.6497, UTILITY: 0.2930, AUTHENTICATION: 0.2930
  // (except EG, NG, ZA which are explicit)
  // ============================================

  // Algeria
  DZ: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Angola
  AO: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Benin
  BJ: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Botswana
  BW: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Burkina Faso
  BF: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Burundi
  BI: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Cameroon
  CM: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Cape Verde
  CV: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Central African Republic
  CF: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Chad
  TD: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Comoros
  KM: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Congo (DRC)
  CD: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Congo (Republic)
  CG: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Côte d'Ivoire
  CI: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Djibouti
  DJ: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Equatorial Guinea
  GQ: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Eritrea
  ER: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Eswatini (Swaziland)
  SZ: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Ethiopia
  ET: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Gabon
  GA: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Gambia
  GM: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Ghana
  GH: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Guinea
  GN: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Guinea-Bissau
  GW: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Kenya
  KE: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Lesotho
  LS: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Liberia
  LR: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Libya
  LY: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Madagascar
  MG: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Malawi
  MW: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Mali
  ML: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Mauritania
  MR: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Mauritius
  MU: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Morocco
  MA: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Mozambique
  MZ: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Namibia
  NA: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Niger
  NE: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Rwanda
  RW: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // São Tomé and Príncipe
  ST: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Senegal
  SN: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Seychelles
  SC: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Sierra Leone
  SL: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Somalia
  SO: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // South Sudan
  SS: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Sudan
  SD: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Tanzania
  TZ: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Togo
  TG: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Tunisia
  TN: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Uganda
  UG: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Zambia
  ZM: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },
  // Zimbabwe
  ZW: { MARKETING: 1.6497, UTILITY: 0.293, AUTHENTICATION: 0.293 },

  // ============================================
  // REST OF ASIA PACIFIC
  // Pricing: MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282
  // (except IN, ID, MY, PK which are explicit)
  // ============================================

  // Afghanistan
  AF: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Bangladesh
  BD: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Bhutan
  BT: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Brunei
  BN: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Cambodia
  KH: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // China
  CN: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Hong Kong
  HK: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Japan
  JP: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Laos
  LA: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Macau
  MO: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Maldives
  MV: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Mongolia
  MN: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Myanmar
  MM: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Nepal
  NP: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // North Korea
  KP: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Philippines
  PH: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Singapore
  SG: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // South Korea
  KR: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Sri Lanka
  LK: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Taiwan
  TW: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Tajikistan
  TJ: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Thailand
  TH: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Timor-Leste
  TL: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Turkmenistan
  TM: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },
  // Vietnam
  VN: { MARKETING: 5.3664, UTILITY: 0.8282, AUTHENTICATION: 0.8282 },

  // ============================================
  // REST OF CENTRAL & EASTERN EUROPE
  // Pricing: MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496
  // (except RU which is explicit)
  // ============================================

  // Albania
  AL: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Armenia
  AM: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Azerbaijan
  AZ: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Belarus
  BY: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Bosnia and Herzegovina
  BA: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Bulgaria
  BG: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Croatia
  HR: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Czech Republic
  CZ: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Estonia
  EE: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Georgia
  GE: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Hungary
  HU: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Kazakhstan
  KZ: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Kosovo
  XK: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Latvia
  LV: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Lithuania
  LT: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Moldova
  MD: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Montenegro
  ME: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // North Macedonia
  MK: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Poland
  PL: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Romania
  RO: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Serbia
  RS: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Slovakia
  SK: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Slovenia
  SI: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Ukraine
  UA: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },
  // Uzbekistan
  UZ: { MARKETING: 6.2997, UTILITY: 1.5496, AUTHENTICATION: 1.5496 },

  // ============================================
  // REST OF WESTERN EUROPE
  // Pricing: MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516
  // (except FR, DE, IT, NL, ES, GB which are explicit)
  // ============================================

  // Andorra
  AD: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Austria
  AT: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Belgium
  BE: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Cyprus
  CY: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Denmark
  DK: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Finland
  FI: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Greece
  GR: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Iceland
  IS: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Ireland
  IE: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Liechtenstein
  LI: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Luxembourg
  LU: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Malta
  MT: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Monaco
  MC: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Norway
  NO: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Portugal
  PT: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // San Marino
  SM: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Sweden
  SE: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Switzerland
  CH: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },
  // Vatican City
  VA: { MARKETING: 4.3378, UTILITY: 1.2516, AUTHENTICATION: 1.2516 },

  // ============================================
  // REST OF LATIN AMERICA
  // Pricing: MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278
  // (except AR, BR, CL, CO, MX, PE which are explicit)
  // ============================================

  // Antigua and Barbuda
  AG: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Bahamas
  BS: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Barbados
  BB: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Belize
  BZ: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Bolivia
  BO: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Costa Rica
  CR: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Cuba
  CU: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Dominica
  DM: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Dominican Republic
  DO: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Ecuador
  EC: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // El Salvador
  SV: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Grenada
  GD: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Guatemala
  GT: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Guyana
  GY: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Haiti
  HT: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Honduras
  HN: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Jamaica
  JM: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Nicaragua
  NI: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Panama
  PA: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Paraguay
  PY: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Puerto Rico
  PR: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Saint Kitts and Nevis
  KN: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Saint Lucia
  LC: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Saint Vincent and the Grenadines
  VC: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Suriname
  SR: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Trinidad and Tobago
  TT: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Uruguay
  UY: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },
  // Venezuela
  VE: { MARKETING: 5.4207, UTILITY: 0.8278, AUTHENTICATION: 0.8278 },

  // ============================================
  // REST OF MIDDLE EAST
  // Pricing: MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665
  // (except EG, IL, SA, AE which are explicit)
  // ============================================

  // Bahrain
  BH: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Iran
  IR: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Iraq
  IQ: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Jordan
  JO: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Kuwait
  KW: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Lebanon
  LB: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Oman
  OM: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Palestine
  PS: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Qatar
  QA: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Syria
  SY: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },
  // Yemen
  YE: { MARKETING: 2.4993, UTILITY: 0.6665, AUTHENTICATION: 0.6665 },

  // ============================================
  // OTHER (REST OF THE WORLD)
  // Pricing: MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641
  // Includes: Oceania and any other unlisted countries
  // ============================================

  // --- OCEANIA ---
  // Australia
  AU: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Fiji
  FJ: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Kiribati
  KI: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Marshall Islands
  MH: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Micronesia
  FM: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Nauru
  NR: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // New Zealand
  NZ: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Palau
  PW: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Papua New Guinea
  PG: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Samoa
  WS: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Solomon Islands
  SB: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Tonga
  TO: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Tuvalu
  TV: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
  // Vanuatu
  VU: { MARKETING: 4.4248, UTILITY: 0.5641, AUTHENTICATION: 0.5641 },
};

/**
 * Default pricing for unknown/unmapped countries
 * Uses "Other" region pricing
 */
export const DEFAULT_PRICING: CategoryPricing = {
  MARKETING: 4.4248,
  UTILITY: 0.5641,
  AUTHENTICATION: 0.5641,
};

/**
 * Get pricing for a country by ISO code
 * Returns default pricing if country not found
 *
 * @param isoCode - Uppercase ISO 2-letter country code (e.g., 'IN', 'US')
 * @returns CategoryPricing object with MARKETING, UTILITY, AUTHENTICATION prices
 */
export const getPricingByCountry = (isoCode: string): CategoryPricing => {
  const upperIso = isoCode.toUpperCase();
  return WHATSAPP_PRICING[upperIso] ?? DEFAULT_PRICING;
};

/**
 * Get price for a specific country and category
 *
 * @param isoCode - Uppercase ISO 2-letter country code
 * @param category - Message category (MARKETING, UTILITY, AUTHENTICATION)
 * @returns Price per message in INR
 */
export const getPrice = (isoCode: string, category: Category): number => {
  const pricing = getPricingByCountry(isoCode);
  return pricing[category];
};
